import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LockedDayFunctionalPermissions, LockedDayPermissions } from '../../models/permissions/permission.models';
import { PtLuxonDate } from '../../models/projects/date.models';
import { ImplementerService } from './implementer.service';
import { CompleteDayService } from '../complete-day/complete-day.service';
import { AppConfig } from '../../app/app.config';
import { DateService } from '../date/date.service';

@Injectable()
export class LockedDayPermissionService {
  config = AppConfig.settings;
  // this service handles permissions only for locked day/implementer specific tasks, therefore -
  // if not in implementer environment, always return true, as the other app permissions will come into play instead

  constructor(
    private http: HttpClient,
    private implementerService: ImplementerService,
    private completeDayService: CompleteDayService,
    private dateService: DateService
  ) {
  }

  public canAddOrRemoveDailyLogOperations(permissions: LockedDayPermissions): boolean {
    if (!this.config.isImplementerEnvironment) {
      return true;
    }

    return permissions.canEditLockedDayPreviousToCurrent || permissions.canEditLockedDaySingleDayOnly || permissions.canEditLockedDayAll;
  }

  public canEditOperationTimes(permissions: LockedDayPermissions): boolean {
    if (!this.config.isImplementerEnvironment) {
      return true;
    }

    return permissions.canEditLockedDayOperationTimesOnly
      || permissions.canEditLockedDayPreviousToCurrent
      || permissions.canEditLockedDaySingleDayOnly
      || permissions.canEditLockedDayAll;
  }

  public canMarkDayComplete(permissions: LockedDayPermissions): boolean {
    if (!this.config.isImplementerEnvironment) {
      return true;
    }

    return permissions.canEditLockedDayOperationTimesOnly
      || permissions.canEditLockedDayPreviousToCurrent
      || permissions.canEditLockedDaySingleDayOnly
      || permissions.canEditLockedDayAll;
  }

  public canEditCurrentLockedDayOnly(permissions: LockedDayPermissions): boolean {
    if (!this.config.isImplementerEnvironment) {
      return true;
    }

    return permissions.canEditLockedDayPreviousToCurrent
      || permissions.canEditLockedDaySingleDayOnly
      || permissions.canEditLockedDayAll;
  }

  public async canImpactFutureDay(permissions: LockedDayPermissions, dailyLogDate: PtLuxonDate, currentActiveDay: PtLuxonDate): Promise<boolean> {
    if (!this.config.isImplementerEnvironment) {
      return true;
    }

    // this might be called without a dailyLogDate or currentActiveDay set, in cases where we don't care about affecting
    // a future day (e.g comment screen) in this case we can just return false as that flag isn't gonna get used anyway
    if (!currentActiveDay || !currentActiveDay.dateTime || !dailyLogDate || !dailyLogDate.dateTime) {
      return false;
    }

    // two scenarios where user can make changes that impact a future day
    // S1: User is editing the locked day immediately prior to the current active day, that has not been completed, and has canEditLockedDayPreviousToCurrent permission
    // S2: User has canEditLockedDayAll permission, and user is not editing the final day of a implementerSnapshot (unless the next day is the current active day)

    // S1
    if (permissions.canEditLockedDayPreviousToCurrent || permissions.canEditLockedDayAll) {
      const currentLocalDayMinus1 = await this.dateService.minusDuration(currentActiveDay.dateTime, {day: 1}, true);

      if (dailyLogDate.dateTime >= currentLocalDayMinus1) {
        return true;
      }
    }

    // S2
    if (permissions.canEditLockedDayAll) {
      const activeImplementerSnapshot = await this.implementerService.getActiveSnapshotSummary(this.config.isVesselEnvironment);
      // if editing a day that isn't the last day of the snapshot, then user can impact future day
      if (dailyLogDate.dateTime < activeImplementerSnapshot.endDate.dateTime) {
        return true;
      }

      const currentLocalDayMinus1 = await this.dateService.minusDuration(currentActiveDay.dateTime, {day: 1}, true);
      // if the current active day is the next day, then user can impact future day
      if (dailyLogDate.dateTime >= currentLocalDayMinus1) {
        return true;
      }
    }

    return false;
  }

  async setFunctionalPermissions(lockedDayPermissions: LockedDayPermissions, dailyLogDate?: PtLuxonDate) {
    let currentActiveDay = null;

    // we only need to check the api for this if implementer environment, so just leave it as null to save us an api call
    if (this.config.isImplementerEnvironment) {
      currentActiveDay = await this.completeDayService.getLatestPtLuxonDate();
    }

    lockedDayPermissions.functionalPermissions = new LockedDayFunctionalPermissions();
    lockedDayPermissions.functionalPermissions.canAddRemoveOperations = this.canAddOrRemoveDailyLogOperations(lockedDayPermissions);
    lockedDayPermissions.functionalPermissions.canEditOperationTimes = this.canEditOperationTimes(lockedDayPermissions);
    lockedDayPermissions.functionalPermissions.canImpactFutureDay = await this.canImpactFutureDay(lockedDayPermissions, dailyLogDate, currentActiveDay);
    lockedDayPermissions.functionalPermissions.canMarkDayComplete = await this.canMarkDayComplete(lockedDayPermissions);
    lockedDayPermissions.functionalPermissions.canAccessOfficeImplementer = lockedDayPermissions.canAccessOfficeImplementer;

    const canEditCurrentDayOnly = await this.canEditCurrentLockedDayOnly(lockedDayPermissions);
    lockedDayPermissions.functionalPermissions.canEditWeatherLog = canEditCurrentDayOnly;
    lockedDayPermissions.functionalPermissions.canEditLegLoadLog = canEditCurrentDayOnly;
    lockedDayPermissions.functionalPermissions.canImpactCurrentLockedDay = canEditCurrentDayOnly;
  }


}



// WEBPACK FOOTER //
// ./src/services/implementer/locked-day-permission.service.ts